/**
 * Fetches Miłosz's gaze network (model.onnx, ~45 MB) for the live lab.
 *
 * The backend hands out a short-lived URL to the private media bucket
 * (GET /api/admin/gaze-lab/model/, admin panel only); the bytes are kept in
 * Cache Storage under the model's sha256 so the lab downloads them once per
 * browser, not once per visit. A mismatching download is rejected rather than
 * run — a truncated file or an S3 error page would otherwise fail much later
 * and much less clearly.
 *
 * VITE_GAZE_MODEL_URL overrides the endpoint for local work with
 * VITE_AUTH_BYPASS (no backend): point it at a file served by Vite.
 */
import { API_CONFIG } from '@/config/api'
import { baseService } from '@/services/baseService'

interface ModelInfo {
  version: string
  sha256: string
  url: string
}

const CACHE_NAME = 'facewise-gaze-model'

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('')
}

async function fromCache(key: string): Promise<Uint8Array | null> {
  try {
    const hit = await (await caches.open(CACHE_NAME)).match(key)
    return hit ? new Uint8Array(await hit.arrayBuffer()) : null
  } catch {
    return null // no Cache Storage (private window, insecure origin) — just download
  }
}

async function toCache(key: string, bytes: Uint8Array): Promise<void> {
  try {
    const cache = await caches.open(CACHE_NAME)
    for (const old of await cache.keys()) await cache.delete(old)
    await cache.put(key, new Response(bytes))
  } catch {
    /* caching is a convenience */
  }
}

export async function loadLabModel(): Promise<{ bytes: Uint8Array; version: string }> {
  const override = import.meta.env.VITE_GAZE_MODEL_URL as string | undefined
  const info: ModelInfo = override
    ? { version: 'local', sha256: '', url: override }
    : (await baseService.get<ModelInfo>(API_CONFIG.ENDPOINTS.ADMIN.GAZE_LAB_MODEL)).data

  const key = `/gaze-model/${info.sha256 || info.url}`
  const cached = await fromCache(key)
  if (cached) return { bytes: cached, version: info.version }

  const response = await fetch(info.url)
  if (!response.ok) throw new Error(`gaze model: HTTP ${response.status}`)
  const bytes = new Uint8Array(await response.arrayBuffer())
  if (info.sha256 && (await sha256Hex(bytes)) !== info.sha256) {
    throw new Error('gaze model: checksum mismatch')
  }
  await toCache(key, bytes)
  return { bytes, version: info.version }
}
